import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { fetcher, endpoints, updateCall, deleteFetcher } from 'src/lib/axios';

import { toast } from 'src/components/snackbar';

import { useHRAuth } from 'src/auth/hooks/use-hr-context';

export interface OrganisationDetails {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  profilePictureUrl: string;
  email: string;
  emailConfirmed: boolean;
  organizationId: number | string;
  organizationName: string;
  organizationSlug: string;
  organizationLogo: string;
  organizationWebsite: string;
}

interface OrganisationContextType extends OrganisationDetails {
  updateProfile: (
    data: Partial<OrganisationDetails>,
    callback?: (data: any, success: boolean) => void
  ) => void;
  updateProfilePicture: (data: FormData, callback?: (data: any, success: boolean) => void) => void;
  updateOrganisation: (
    data: Record<string, string>,
    callback?: (data: any, success: boolean) => void
  ) => void;
  updateOrganisationLogo: (
    data: FormData,
    callback?: (data: any, success: boolean) => void
  ) => void;
  deleteOrganisationLogo: (
    callback?: (data: any, success: boolean) => void
  ) => void;
  deleteProfilePicture: (
    callback?: (data: any, success: boolean) => void
  ) => void;
}

const OrganisationContext = createContext<OrganisationContextType | null>(null);

/**
 * Maps raw API response to our OrganisationDetails type
 */
const mapApiResponseToOrganisation = (res: any): OrganisationDetails => ({
  id: res?.Id ?? '',
  firstName: res?.FirstName ?? '',
  lastName: res?.LastName ?? '',
  displayName: res?.DisplayName ?? '',
  profilePictureUrl: res?.ProfilePictureUrl ?? '',
  email: res?.Email ?? '',
  emailConfirmed: res?.EmailConfirmed ?? false,
  organizationId: res?.Organizations?.[0]?.OrganizationId ?? '',
  organizationName: res?.Organizations?.[0]?.Name ?? '',
  organizationSlug: res?.Organizations?.[0]?.Slug ?? '',
  organizationLogo: res?.Organizations?.[0]?.Logo ?? '',
  organizationWebsite: res?.Organizations?.[0]?.Url ?? '',
});

export const OrganisationProvider = ({ children }: { children: React.ReactNode }) => {
  const [organisation, setOrganisation] = useState<OrganisationDetails | null>(null);
  const { Token } = useHRAuth();

  const fetchOrganisation = async () => {
    try {
      const res = await fetcher(endpoints.account.profile);
      setOrganisation(mapApiResponseToOrganisation(res));
    } catch (error) {
      setOrganisation(null);
      // toast.error('Failed', {
      //   description: 'Failed to fetch organisation',
      // });
    }
  };

  useEffect(() => {
    if (Token) {
      fetchOrganisation();
    }
  }, [Token]);

  useEffect(() => {
    if (!organisation) return;
  
    datadogRum.setUser({
      id: organisation.id,                
      name: organisation.displayName,     
      email: organisation.email        
    });

    datadogRum.setGlobalContextProperty(
      'organizationId',
      organisation.organizationId
    );
    datadogRum.setGlobalContextProperty(
      'organizationSlug',
      organisation.organizationSlug
    );
  
    datadogLogs.setGlobalContextProperty(
      'organizationId',
      organisation.organizationId
    );
    datadogLogs.setGlobalContextProperty(
      'organizationSlug',
      organisation.organizationSlug
    );

  }, [organisation]);

  const updateProfile = useCallback(
    async (
      data: Partial<OrganisationDetails>,
      callback?: (data: any, success: boolean) => void
    ) => {
      try {
        const res = await updateCall(endpoints.account.profile, data, {}, 'PUT');
        setOrganisation(mapApiResponseToOrganisation(res));
        toast.success('Success', {
          description: 'Profile updated successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Failed to update profile details.',
        });
        callback?.(data, false);
      }
    },
    []
  );

  const updateProfilePicture = useCallback(
    async (data: FormData, callback?: (data: any, success: boolean) => void) => {
      try {
        const res = await updateCall(
          endpoints.account.profilePicture,
          data,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          'PUT'
        );
        setOrganisation(mapApiResponseToOrganisation(res));
        toast.success('Success', {
          description: 'Profile picture updated successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Failed to update profile picture.',
        });
        callback?.(data, false);
      }
    },
    []
  );

  const deleteProfilePicture = useCallback(
    async (callback?: (data: any, success: boolean) => void) => {
      try {
        const res = await deleteFetcher(endpoints.account.profilePicture);
        setOrganisation((prev: OrganisationDetails | null) => {
          if (!prev) return null;
          return {
            ...prev,
            profilePictureUrl: '',
          };
        });
        toast.success('Success', {
          description: 'Profile picture deleted successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Something went wrong. Please try again later.',
        });
        callback?.(null, false);
      }
    },
    []
  );

  const updateOrganisation = useCallback(
    async (data: Record<string, string>, callback?: (data: any, success: boolean) => void) => {
      try {
        const res = await updateCall(
          `${endpoints.account.organisation}/${organisation?.organizationId}`,
          data,
          {},
          'PUT'
        );
        setOrganisation((prev: OrganisationDetails | null) => {
          if (!prev) return null;
          return {
            ...prev,
            organizationId: prev.organizationId, // ID never changes on org update
            organizationName: res?.Name || '',
            organizationSlug: res?.Slug || '',
            organizationWebsite: res?.Url || '',
          };
        });
        toast.success('Success', {
          description: 'Organisation updated successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Failed to update organisation.',
        });
        callback?.(data, false);
      }
    },
    [organisation]
  );

  const deleteOrganisationLogo = useCallback(
    async (callback?: (data: any, success: boolean) => void) => {
      try {
        const res = await deleteFetcher(`${endpoints.account.organisation}/${organisation?.organizationId}/Logo`);
        setOrganisation((prev: OrganisationDetails | null) => {
          if (!prev) return null;
          return {
            ...prev,
            organizationLogo: '',
          };
        });
        toast.success('Success', {
          description: 'Organisation logo deleted successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Something went wrong. Please try again later.',
        });
        callback?.(null, false);
      }
    },
    [organisation]
  );

  const updateOrganisationLogo = useCallback(
    async (data: FormData, callback?: (data: any, success: boolean) => void) => {
      try {
        const res = await updateCall(
          `${endpoints.account.organisation}/${organisation?.organizationId}/Logo`,
          data,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
          'PUT'
        );
        setOrganisation((prev: OrganisationDetails | null) => {
          if (!prev) return null;
          return {
            ...prev,
            organizationId: prev.organizationId, // ID never changes on logo update
            organizationName: res?.Name || '',
            organizationSlug: res?.Slug || '',
            organizationLogo: res?.Logo || '',
          };
        });
        toast.success('Success', {
          description: 'Organisation logo updated successfully.',
        });
        callback?.(res, true);
      } catch (error) {
        toast.error('Failed', {
          description: 'Failed to update organisation logo.',
        });
        callback?.(data, false);
      }
    },
    [organisation]
  );

  const memoizedValue = useMemo<OrganisationContextType>(
    () => ({
      id: organisation?.id || '',
      firstName: organisation?.firstName || '',
      lastName: organisation?.lastName || '',
      displayName: organisation?.displayName || '',
      email: organisation?.email || '',
      profilePictureUrl: organisation?.profilePictureUrl || '',
      emailConfirmed: organisation?.emailConfirmed || false,
      organizationId: organisation?.organizationId || '',
      organizationName: organisation?.organizationName || '',
      organizationSlug: organisation?.organizationSlug || '',
      organizationLogo: organisation?.organizationLogo || '',
      organizationWebsite: organisation?.organizationWebsite || '',
      updateProfile,
      updateProfilePicture,
      updateOrganisation,
      updateOrganisationLogo,
      deleteOrganisationLogo,
      deleteProfilePicture,
    }),
    [organisation, updateProfile, updateProfilePicture, updateOrganisation, updateOrganisationLogo, deleteOrganisationLogo, deleteProfilePicture]
  );

  return (
    <OrganisationContext.Provider value={memoizedValue}>{children}</OrganisationContext.Provider>
  );
};

export const useOrganisation = () => {
  const context = useContext(OrganisationContext);
  if (!context) {
    throw new Error('useOrganisation must be used within an OrganisationProvider');
  }
  return context;
};
